// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronsUpDown from "@insite/mobius/Icons/ChevronsUpDown";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@steiner/MobiusOverrides/OverflowMenu";
import { actionStyles } from "@steiner/Overrides/Widgets/MyListsDetails/MyListsDetailsActions";
import Star from "@steiner/XDL/Icons/Star";
import StarX from "@steiner/XDL/Icons/StarX";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import {
    color,
    createLinkIconStyles,
    linkSizeVariants,
    modalButtonsActionStyles,
    xScreen,
} from "@steiner/XDL/Utilities";
import { css } from "styled-components";

interface MoreActionsSharedStyle {
    overflowMenu?: OverflowMenuPresentationProps;
    moreActionsLink?: LinkPresentationProps;
}

export const moreActionsSharedStyle: MoreActionsSharedStyle = {
    overflowMenu: {
        _width: 140,
        cssOverrides: {
            wrapper: css`
                width: auto;
                height: auto;
                & button[aria-expanded="true"] ${IconWrapper} svg {
                    transform: rotate(180deg);
                }
            `,
            menu: css`
                padding: 4px 0;
                li:last-child button {
                    padding-bottom: 8px;
                    &::after {
                        display: none;
                    }
                }
            `,
            menuItem: css`
                padding: 8px 12px 0;
                font-size: 12px;
                line-height: 17px;
                color: ${color("text.main")};
                font-weight: bold;
                background: ${color("common.white")};
                &:hover {
                    color: ${color("common.white")};
                    background: ${color("common.ui9")};
                }
                &::after {
                    content: "";
                    display: block;
                    border-bottom: 1px solid ${color("common.border")};
                    width: 100%;
                    margin-top: 8px;
                }
            `,
        },
    },
    moreActionsLink: {
        ...createLinkIconStyles({
            icon: ChevronsUpDown,
            linkStyles: {
                icon: {
                    position: "right",
                },
            },
        }),
    },
};

interface FavoriteClickableSharedStyles {
    favoriteClickable?: ClickablePresentationProps;
    star?: IconPresentationProps;
    starX?: IconPresentationProps;
}

export const favoriteClickableSharedStyles: FavoriteClickableSharedStyles = {
    star: {
        src: Star,
        size: linkSizeVariants.small.iconSize,
        css: css`
            margin-right: 8px;
        `,
    },
    starX: {
        src: StarX,
        size: linkSizeVariants.small.iconSize,
        color: "primary",
        css: css`
            margin-right: 8px;
        `,
    },
    favoriteClickable: {
        css: css`
            font-size: ${linkSizeVariants.small.size}px;
            font-weight: ${fontConfig.weight.semiBold};
            line-height: ${linkSizeVariants.small.lineHeight}px;
            ${({ favorite }: any) =>
                favorite &&
                css`
                    color: ${color("primary")};
                `};
            &:hover,
            &:hover ${IconWrapper} {
                color: ${color("common.ui9")};
            }
        `,
    },
};

actionStyles.wrapper = {
    css: css`
        display: flex;
        justify-content: space-between;
        column-gap: 32px;
        border-bottom: 1px solid ${color("common.border")};
        margin-bottom: 12px;
        padding-bottom: 12px;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
            row-gap: 20px;
        }
    `,
};

actionStyles.actionsWrapperRight = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        gap: 12px;
        max-width: 50%;
        ${xScreen("lte", "sm")} {
            max-width: 100%;
        }
    `,
};

actionStyles.buttonWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        gap: 12px;
        ${xScreen("lte", "sm")} {
            flex-direction: column;
            align-items: flex-start;
        }
    `,
};

actionStyles.listInfoWrapper = {
    css: css`
        display: flex;
        justify-content: flex-end;
        align-items: flex-start;
        gap: 48px;
    `,
};

actionStyles.innerButtonWrapper = {
    css: css`
        display: flex;
        gap: 12px;
        align-items: end;
        flex-wrap: wrap;
        ${xScreen("lte", "xs")} {
            flex-direction: column;
            width: 100%;
        }
    `,
};

actionStyles.statusContainer = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 12px;
        align-items: center;
    `,
};

actionStyles.addItemButton = {
    typographyProps: {
        css: css`
            white-space: nowrap;
        `,
    },
    variant: "secondary",
};

actionStyles.removeSelectedButton = {
    typographyProps: {
        css: css`
            white-space: nowrap;
        `,
    },
    variant: "secondary",
};

actionStyles.addListButton = {
    typographyProps: {
        css: css`
            white-space: nowrap;
        `,
    },
    css: css`
        ${xScreen("lte", "xs")} {
            width: 100%;
        }
    `,
};

actionStyles.editListModal = {
    sizeVariant: "small",
};

actionStyles.copyListModal = {
    sizeVariant: "small",
};

actionStyles.actionsWrapperLeft = {
    css: css`
        max-width: 50%;
        ${xScreen("lte", "sm")} {
            max-width: none;
        }
    `,
};

actionStyles.listActionsWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 12px;
    `,
};

actionStyles.nameText = {
    variant: "h1",
    color: "primary",
};

actionStyles.wishlistCountWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        ${xScreen("lte", "sm")} {
            flex-direction: row;
            gap: 12px;
        }
    `,
};

actionStyles.wishlistLabel = {
    size: fontConfig.fontSize.small,
    lineHeight: fontConfig.lineHeight.small,
};

actionStyles.wishlistCount = {
    size: fontConfig.fontSize.regular,
    weight: fontConfig.weight.bold,
    lineHeight: fontConfig.lineHeight.regular,
    color: "primary",
};

actionStyles.iconContainer = {
    css: css`
        width: fit-content;
    `,
};

actionStyles.actionFavoriteClickable = {
    ...favoriteClickableSharedStyles.favoriteClickable,
};

actionStyles.starIcon = {
    ...favoriteClickableSharedStyles.star,
};

actionStyles.starXIcon = {
    ...favoriteClickableSharedStyles.starX,
};

actionStyles.overflowMenu = {
    ...moreActionsSharedStyle.overflowMenu,
};

actionStyles.moreActions = {
    ...moreActionsSharedStyle.moreActionsLink,
};

const customClickable = {
    css: css`
        font-size: ${linkSizeVariants.small.size}px;
        line-height: ${linkSizeVariants.small.lineHeight};
        font-weight: ${fontConfig.weight.semiBold};
        padding: 0;
        margin: 0;
        color: ${color("text.link")};
        &:hover {
            color: ${color("common.ui9")};
        }
    `,
};

actionStyles.printButton = {
    ...customClickable,
};

actionStyles.addTagsButton = {
    ...customClickable,
};

actionStyles.scheduleButton = {
    ...customClickable,
};

actionStyles.editButton = {
    ...customClickable,
};

actionStyles.shareButton = {
    ...customClickable,
};

actionStyles.hideButton = {
    ...customClickable,
};

actionStyles.copyButton = {
    ...customClickable,
};

actionStyles.exportButton = {
    ...customClickable,
};

actionStyles.deleteButton = {
    ...customClickable,
};

actionStyles.deleteListModal = {
    submitButton: {
        ...modalButtonsActionStyles.submitButton,
    },
};
